import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { dashboardService, DashboardStats, ActivityData } from '../../services/dashboardService';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div<{ $bgColor: string }>`
  width: 60px;
  height: 60px;
  border-radius: var(--border-radius-lg);
  background: ${props => props.$bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-gray-800);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  font-weight: 500;
`;

const StatChange = styled.div<{ $positive: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$positive ? '#10B981' : '#EF4444'};
  font-weight: 600;
  margin-top: 0.25rem;
`;

const ChartCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-gray-800);
  }
`;

const RecentActivityCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  grid-column: 1 / -1;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--color-gray-100);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  background: ${props => 
    props.$type === 'story' ? '#EBF8FF' :
    props.$type === 'user' ? '#F0FDF4' :
    props.$type === 'report' ? '#FEF3C7' :
    '#FDF2F8'
  };
  color: ${props => 
    props.$type === 'story' ? '#3B82F6' :
    props.$type === 'user' ? '#10B981' :
    props.$type === 'report' ? '#F59E0B' :
    '#EC4899'
  };
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 600;
  color: var(--color-gray-800);
  margin-bottom: 0.25rem;
`;

const ActivityMeta = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ActionButton = styled.button`
  padding: 1rem;
  background: var(--color-white);
  border: 2px solid var(--color-gray-200);
  border-radius: var(--border-radius-lg);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  color: var(--color-gray-700);
  font-weight: 500;
  
  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
  
  .icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    display: block;
  }
`;


const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalStories: 0,
    pendingStories: 0,
    totalReports: 0,
    aiDetections: 0,
    userGrowth: 0,
    activeUserGrowth: 0,
    storyGrowth: 0,
    suspendedUsers: 0,
    bannedUsers: 0,
    newUsersLastWeek: 0,
    newUsersLastMonth: 0,
    approvedStories: 0,
    rejectedStories: 0,
    appealPendingStories: 0,
    storiesWithAIAnalysis: 0,
    inappropriateContentDetected: 0,
  });

  const [activityData, setActivityData] = useState<ActivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Chart data states
  const [userActivityData, setUserActivityData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Active Users',
        data: [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
    ],
  });

  const [contentData, setContentData] = useState({
    labels: ['Stories', 'Comments', 'Reports'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
        borderWidth: 0,
      },
    ],
  });

  const [moderationData, setModerationData] = useState({
    labels: ['Auto-Approved', 'Manual Review', 'Rejected'],
    datasets: [
      {
        label: 'Content Moderation',
        data: [0, 0, 0],
        backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
      },
    ],
  });

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load real dashboard data from legacy endpoints
        const [dashboardStats, userActivity, contentStats, recentActivity] = await Promise.allSettled([
          dashboardService.getDashboardStats(),
          dashboardService.getUserActivityData(),
          dashboardService.getContentStats(),
          dashboardService.getRecentActivity(),
        ]);

        // Transform and set dashboard statistics
        if (dashboardStats.status === 'fulfilled') {
          const transformedStats = dashboardService.transformToStats(dashboardStats.value);
          setStats(transformedStats);
        }

        // Set recent activity data
        if (recentActivity.status === 'fulfilled') {
          setActivityData(recentActivity.value);
        }

        // Update user activity chart
        if (userActivity.status === 'fulfilled') {
          const chartData = dashboardService.transformUserActivityToChart(userActivity.value);
          setUserActivityData(prev => ({
            ...prev,
            labels: chartData.labels,
            datasets: [{
              ...prev.datasets[0],
              data: chartData.values,
            }],
          }));
        }

        // Update content distribution chart
        if (contentStats.status === 'fulfilled') {
          const { stories, comments, reports } = dashboardService.transformContentStatsToChart(contentStats.value);
          setContentData(prev => ({
            ...prev,
            datasets: [{
              ...prev.datasets[0],
              data: [stories, comments, reports],
            }],
          }));
        }

        // Update moderation chart
        if (contentStats.status === 'fulfilled') {
          const { approved, review, rejected } = dashboardService.transformContentStatsToModeration(contentStats.value);
          setModerationData(prev => ({
            ...prev,
            datasets: [{
              ...prev.datasets[0],
              data: [approved, review, rejected],
            }],
          }));
        }

      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  return (
    <div>
      {/* Quick Actions */}
      <QuickActions>
        <ActionButton onClick={() => navigate('/admin/stories')}>
          <span className="icon">📚</span>
          Review Stories
        </ActionButton>
        <ActionButton onClick={() => navigate('/admin/users')}>
          <span className="icon">👥</span>
          Manage Users
        </ActionButton>
        <ActionButton onClick={() => navigate('/admin/reports')}>
          <span className="icon">🚨</span>
          View Reports
        </ActionButton>
        <ActionButton onClick={() => navigate('/admin/ai-settings')}>
          <span className="icon">🤖</span>
          AI Settings
        </ActionButton>
        <ActionButton onClick={() => navigate('/admin/settings')}>
          <span className="icon">🖼️</span>
          Image Settings
        </ActionButton>
      </QuickActions>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading dashboard data...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ 
          background: '#FEE2E2', 
          color: '#DC2626', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1rem' 
        }}>
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard>
          <StatIcon $bgColor="#EBF8FF">👥</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.totalUsers.toLocaleString()}</StatValue>
            <StatLabel>Total Users</StatLabel>
            <StatChange $positive={stats.userGrowth >= 0}>
              {loading ? '---' : `${stats.userGrowth > 0 ? '+' : ''}${stats.userGrowth.toFixed(1)}% from last month`}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $bgColor="#F0FDF4">🟢</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.activeUsers.toLocaleString()}</StatValue>
            <StatLabel>Active Users (7d)</StatLabel>
            <StatChange $positive={stats.activeUserGrowth >= 0}>
              {loading ? '---' : `${stats.activeUserGrowth > 0 ? '+' : ''}${stats.activeUserGrowth.toFixed(1)}% from last week`}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $bgColor="#FDF2F8">📚</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.totalStories.toLocaleString()}</StatValue>
            <StatLabel>Total Stories</StatLabel>
            <StatChange $positive={stats.storyGrowth >= 0}>
              {loading ? '---' : `${stats.storyGrowth > 0 ? '+' : ''}${stats.storyGrowth.toFixed(1)}% from last month`}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $bgColor="#FEF3C7">⏳</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.pendingStories}</StatValue>
            <StatLabel>Pending Review</StatLabel>
            <StatChange $positive={false}>
              {loading ? '---' : stats.pendingStories > 5 ? `${Math.floor(stats.pendingStories * 0.3)} urgent` : 'Low priority'}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $bgColor="#FEE2E2">🚨</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.totalReports}</StatValue>
            <StatLabel>Open Reports</StatLabel>
            <StatChange $positive={false}>
              {loading ? '---' : stats.totalReports > 10 ? `${Math.floor(stats.totalReports * 0.4)} high priority` : 'Normal levels'}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $bgColor="#EDE9FE">🤖</StatIcon>
          <StatContent>
            <StatValue>{loading ? '---' : stats.aiDetections}</StatValue>
            <StatLabel>AI Detections (24h)</StatLabel>
            <StatChange $positive={true}>
              {loading ? '---' : '92% accuracy'}
            </StatChange>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Charts */}
      <DashboardGrid>
        <ChartCard>
          <h3>User Activity (7 days)</h3>
          <Line 
            data={userActivityData} 
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard>
          <h3>Content Distribution</h3>
          <Doughnut 
            data={contentData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom' as const,
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard>
          <h3>Content Moderation (30 days)</h3>
          <Bar 
            data={moderationData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>
      </DashboardGrid>

      {/* Recent Activity */}
      <RecentActivityCard>
        <h3>Recent Activity</h3>
        {activityData.map(activity => (
          <ActivityItem key={activity.id}>
            <ActivityIcon $type={activity.type}>
              {activity.icon}
            </ActivityIcon>
            <ActivityContent>
              <ActivityTitle>{activity.title}</ActivityTitle>
              <ActivityMeta>{activity.meta}</ActivityMeta>
            </ActivityContent>
          </ActivityItem>
        ))}
      </RecentActivityCard>
    </div>
  );
};

export default AdminDashboard;